import React from "react";
import s from "./Typography.module.scss";
import classNames from "classnames";
import { SemanticTextLevels, UtilityLevels } from "../../types/colors";
import {
  TypographyTags,
  TypographyTagsTypes,
  TypographyVariantsTypes,
} from "../../types/typography";
import semanticVariables from "ui-v2/styles/semantic_colors.module.scss";
import utilityVariables from "ui-v2/styles/utility_colors.module.scss";

type TypographyProps = {
  label: string | number;
  lineClamp?: number;
  variant: TypographyVariantsTypes;
  color?: SemanticTextLevels | UtilityLevels;
  as?: TypographyTagsTypes;
  style?: React.CSSProperties;
  className?: string;
};

/**
 * Typography
 *
 * @param label label text to be displayed
 * @param variant font style e.g. "label_2_regular" | "label_1_regular" | "label_1_semi_bold" ...
 * @param color font color e.g. "primary" | "negative" | "positive" ...
 * @param as html tag to render e.g. p | span | div | h1 | h2 | h3 | h4 | h5 | h6
 * @param style custom css styles
 * @param className custom class name
 * */
export function Typography({
  label,
  variant,
  color = "primary",
  lineClamp = null,
  style = {},
  className = "",
  as = "p",
  ...rest
}: TypographyProps) {
  const variantClassName = classNames(s[variant], {
    [className]: !!className,
  });

  let typographyStyle = {
    color:
      semanticVariables?.[`semantic_text_${color}`] ||
      utilityVariables?.[`utility_text_${color}`],
    ...style,
  };
  if (lineClamp) {
    typographyStyle = {
      ...typographyStyle,
      display: "-webkit-box",
      overflow: "hidden",
      // @ts-ignore
      ["-webkit-box-orient"]: "vertical",
      // @ts-ignore
      ["-webkit-line-clamp"]: `${lineClamp}`,
    };
  }

  switch (TypographyTags?.[as]) {
    case "p":
      return (
        <p className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </p>
      );
    case "div":
      return (
        <div className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </div>
      );
    case "span":
      return (
        <span className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </span>
      );
    case "h1":
      return (
        <h1 className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </h1>
      );
    case "h2":
      return (
        <h2 className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </h2>
      );
    case "h3":
      return (
        <h3 className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </h3>
      );
    case "h4":
      return (
        <h4 className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </h4>
      );
    case "h5":
      return (
        <h5 className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </h5>
      );
    case "h6":
      return (
        <h6 className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </h6>
      );
    default:
      return (
        <div className={variantClassName} style={typographyStyle} {...rest}>
          {label}
        </div>
      );
  }
}
